import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { EventField, FieldType } from '../../models/event-field-type.interface';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { CartStateService } from '../../services';
import { AddToCartRequest,  Cart } from '../../models';
import { TicketTypeAddOnSelection } from '../../models/event-ticket-type.interface';
import { Router } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { HttpErrorResponse } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { AuthStateService } from '@hypenation/auth';
import { NzNotificationModule, NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'hypenation-tickets-add-to-cart',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzRadioModule,
    NzInputModule,
    NzInputNumberModule,
    NzTimePickerModule,
    NzDatePickerModule,
    NzSelectModule,
    NzModalModule,
    NzIconModule,
    NzNotificationModule
  ],
  templateUrl: './add-to-cart.component.html',
  styleUrl: './add-to-cart.component.css',
  providers: [NzModalService, DatePipe]
})
export class AddToCartComponent implements OnInit {
  readonly nzModalData = inject(NZ_MODAL_DATA);
  fields!: EventField[];
  productUid!: string;
  parentUid!: string;
  userProfile: any = null;
  selectedAddOns: TicketTypeAddOnSelection[] = [];
  dynamicForm!: FormGroup;
  loading = false;
  cart: Cart | null = null;

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _modalRef: NzModalRef,
    private readonly _modal: NzModalService,
    private readonly _router: Router,
    private readonly _datePipe: DatePipe,
    private readonly _authStateService: AuthStateService,
    private readonly _cartStateService: CartStateService,
    private readonly _notification: NzNotificationService
  ) {}

  ngOnInit(): void {
    // Assign fields from modal data
    this.fields = this.nzModalData?.fields || [];
    this.parentUid = this.nzModalData?.parentUid || '';
    this.productUid = this.nzModalData?.productUid || '';
    this.selectedAddOns = this.nzModalData?.selectedAddOns || [];
    this.userProfile = this.nzModalData?.userProfile || null;

    // Sort by positionOrder ascending before initializeForm runs, so the
    // FormArray index used by onSubmit's positional `this.fields[index]`
    // lookup matches the rendered order. Missing values sort last.
    this.fields = [...this.fields].sort(
      (a, b) =>
        (a.positionOrder ?? Number.POSITIVE_INFINITY) -
        (b.positionOrder ?? Number.POSITIVE_INFINITY)
    );

    this.resolveCart();

    // The modal only opens when the ticket type has registration fields
    // that must be filled in per attendee, so each submission adds exactly
    // one ticket. Users add more by re-opening the modal with fresh field
    // values.
    this.dynamicForm = this._fb.group({
      eventFields: this._fb.array([]),
    });

    if (this.fields.length) {
      this.initializeForm();
    } else {
      console.error('No fields available to generate the form');
    }
  }

  private resolveCart(): void {
    this._cartStateService.resolveCart().subscribe({
      next: (cart) => {
        this.cart = cart;
      },
      error: (err) => {
        console.error('Error resolving cart:', err);
      },
    });
  }

  initializeForm(): void {
    const eventFieldsArray = this.dynamicForm.get('eventFields') as FormArray;

    this.fields.forEach((field) => {
      const defaultValue = this.getDefaultValueForField(field);
      const validators = field.mandatory ? [this.getRequiredValidatorForField(field)] : [];
      const formGroup = this._fb.group({
        [field.name]: [defaultValue, validators],
      });

      eventFieldsArray.push(formGroup);
    });
  }

  private getRequiredValidatorForField(field: EventField): ValidatorFn {
    if (field.fieldType === FieldType.BOOLEAN) {
      // Required boolean fields should accept both true and false once selected.
      return (control: AbstractControl) =>
        control.value === null || control.value === undefined ? { required: true } : null;
    }

    if (field.fieldType === FieldType.MULTIPLE_SELECT) {
      return (control: AbstractControl) =>
        Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };
    }

    return Validators.required;
  }

  private getDefaultValueForField(field: EventField): any {
    if (field.fieldType === FieldType.MULTIPLE_SELECT) {
      return [];
    }

    if (!this.userProfile) {
      return null;
    }

    // Check if userProfile is a flat key-value map (from HAC member details API)
    // This is the new structure: { "firstname": "John", "lastname": "Doe", ... }
    if (!this.userProfile.user && typeof this.userProfile === 'object') {
      return this.getValueFromFlatProfile(field);
    }

    // Legacy structure: userProfile.user (for backward compatibility)
    if (!this.userProfile?.user) {
      return null;
    }

    const user = this.userProfile.user;
    const fieldName = field.name.toLowerCase().replace(/[_-]/g, '');

    // Map field names to user profile properties
    switch (fieldName) {
      case 'firstname':
        // Try to extract first name from full name
        if (user.name) {
          const nameParts = user.name.trim().split(/\s+/);
          return nameParts[0] || null;
        }
        return null;

      case 'lastname':
        // Try to extract last name from full name
        if (user.name) {
          const nameParts = user.name.trim().split(/\s+/);
          return nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;
        }
        return null;

      case 'email':
        return user.email || null;

      case 'username':
        return user.username || null;

      case 'dob':
      case 'dateofbirth':
        // Date of birth is not available in the user profile
        return null;

      case 'phonenumber':
      case 'phone':
        // Phone number is not available in the user profile
        return null;

      case 'nationalid':
      case 'idnumber':
        // National ID is not available in the user profile
        return null;

      default:
        // For any other field, try to match by name directly
        return user[field.name] || null;
    }
  }

  /**
   * Get value from flat profile object (HAC member details API response)
   * Matches field names to keys in the profile object with case-insensitive matching
   */
  private getValueFromFlatProfile(field: EventField): any {
    const fieldName = field.name;
    
    // Try exact match first
    if (this.userProfile.hasOwnProperty(fieldName)) {
      return this.userProfile[fieldName];
    }

    // Try case-insensitive match
    const fieldNameLower = fieldName.toLowerCase();
    const matchingKey = Object.keys(this.userProfile).find(
      key => key.toLowerCase() === fieldNameLower
    );
    
    if (matchingKey) {
      return this.userProfile[matchingKey];
    }

    // Try matching with normalized field name (remove underscores, hyphens, spaces)
    const normalizedFieldName = fieldName.toLowerCase().replace(/[_\s-]/g, '');
    const normalizedMatchingKey = Object.keys(this.userProfile).find(
      key => key.toLowerCase().replace(/[_\s-]/g, '') === normalizedFieldName
    );

    if (normalizedMatchingKey) {
      return this.userProfile[normalizedMatchingKey];
    }

    // Try common field name variations
    const fieldNameVariations = this.getFieldNameVariations(fieldName);
    for (const variation of fieldNameVariations) {
      if (this.userProfile.hasOwnProperty(variation)) {
        return this.userProfile[variation];
      }
      
      // Case-insensitive variation match
      const variationLower = variation.toLowerCase();
      const variationMatchingKey = Object.keys(this.userProfile).find(
        key => key.toLowerCase() === variationLower
      );
      
      if (variationMatchingKey) {
        return this.userProfile[variationMatchingKey];
      }
    }

    return null;
  }

  /**
   * Get common variations of a field name for matching
   */
  private getFieldNameVariations(fieldName: string): string[] {
    const lower = fieldName.toLowerCase();
    const variations: string[] = [];

    // Common field name mappings
    const fieldMappings: { [key: string]: string[] } = {
      'firstname': ['firstName', 'first_name', 'first name', 'fname'],
      'lastname': ['lastName', 'last_name', 'last name', 'lname', 'surname'],
      'email': ['emailAddress', 'email_address', 'e-mail'],
      'phonenumber': ['phone', 'phoneNumber', 'phone_number', 'mobile', 'mobileNumber', 'mobile_number'],
      'dateofbirth': ['dob', 'dateOfBirth', 'date_of_birth', 'birthdate', 'birthDate'],
      'nationalid': ['nationalId', 'national_id', 'idnumber', 'idNumber', 'id_number', 'nationalIdNumber'],
    };

    // Check if we have predefined variations
    for (const [key, values] of Object.entries(fieldMappings)) {
      if (lower.includes(key) || key.includes(lower.replace(/[_\s-]/g, ''))) {
        variations.push(...values);
      }
    }

    return variations;
  }

  get eventFieldsArray(): FormArray {
    return this.dynamicForm.get('eventFields') as FormArray;
  }

  getFieldControl(index: number): AbstractControl | null {
    const group = this.eventFieldsArray.at(index) as FormGroup;
    return group.get(this.fields[index].name);
  }

  isFieldInvalid(index: number): boolean {
    const control = this.getFieldControl(index);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  onSubmit(): void {
    type KeyValueObject = { [key: string]: any };
    
    if (this.dynamicForm.valid) {
      console.log(this.dynamicForm.value['eventFields']);
  
      const allFields: KeyValueObject = this.dynamicForm.value[
        'eventFields'
      ].reduce((acc: KeyValueObject, current: KeyValueObject, index: number) => {
        const field = this.fields[index];
        let value = current[field.name];
  
        // Apply transformations based on field type
        if (field.fieldType === FieldType.DATE) {
          value = this._datePipe.transform(value, 'dd-MM-yyyy');
        } else if (field.fieldType === FieldType.TIME) {
          value = this._datePipe.transform(value, 'HH:mm');
        }
  
        return { ...acc, [field.name]: value };
      }, {});

      // Add HAC member number to purchaseDetails if it's a HAC app and member number exists
      const purchaseDetails: KeyValueObject = { ...allFields };
      if (this._authStateService.getAppType() === 'hac') {
        const hacMemberNumber = this._authStateService.getHacMemberNumber();
        if (hacMemberNumber) {
          purchaseDetails['hacMemberNumber'] = hacMemberNumber;
        }
      }
  
      const payload: AddToCartRequest = {
        parentUid: this.parentUid,
        productUid: this.productUid,
        purchaseDetails: purchaseDetails,
        selectedAddOns: this.selectedAddOns,
        // Always 1 — the modal collects per-attendee fields, so each
        // submission adds a single ticket. Users add more by re-opening
        // the modal.
        quantity: 1,
      };
  
      // No resolved cart — the session/cart pointer is poisoned. Rotate and
      // resolve a fresh cart, then proceed, instead of silently no-oping.
      if (!this.cart) {
        this.loading = true;
        this._cartStateService
          .rotateAndResolve()
          .pipe(take(1))
          .subscribe({
            next: (fresh) => {
              if (!fresh) {
                this.loading = false;
                this._notification.error(
                  'Adding Failed',
                  'We could not start a cart session. Please try again.'
                );
                return;
              }
              this.cart = fresh;
              this.proceedWithAdd(payload);
            },
            error: () => {
              this.loading = false;
              this._notification.error(
                'Adding Failed',
                'We could not start a cart session. Please try again.'
              );
            },
          });
        return;
      }

      this.proceedWithAdd(payload);
    } else {
      this.dynamicForm.markAllAsTouched();
      this._notification.warning(
        'Missing required fields',
        'Please fill in all required fields before submitting.',
        { nzDuration: 4000, nzAnimate: true }
      );
      return;
    }
  }

  /**
   * Run the add against a resolved cart. Both branches go through the
   * recovery-aware service entry points so a stale cart UID self-heals
   * (rotate → fresh cart → retry once). The wrappers own notifyCartUpdate(),
   * so we no longer emit it here.
   */
  private proceedWithAdd(payload: AddToCartRequest): void {
    if (!this.cart) {
      return;
    }

    if (
      this.cart.numberOfItems > 0 &&
      this.cart.parentUid &&
      this.cart.parentUid !== this.parentUid
    ) {
      this._modal.confirm({
        nzTitle: 'Different Event in Cart',
        nzContent:
          'You can only purchase tickets from one event at a time. Your cart currently contains tickets for another event. Would you like to clear your cart and proceed with this new ticket?',
        nzOkText: 'Clear Cart & Add',
        nzOkType: 'primary',
        nzOkDanger: true,
        nzCancelText: 'Cancel',
        nzOnOk: () => {
          this.loading = true;
          this._cartStateService
            .clearCartAndAddWithRecovery(payload, this.cart!.uid)
            .subscribe({
              next: () => {
                this.loading = false;
                this._notification.success(
                  'Cart Cleared',
                  'Successfully added the new ticket.'
                );
                this._modalRef.close(true);
              },
              error: (err: HttpErrorResponse) => {
                this.loading = false;
                this._notification.error(
                  'Error',
                  err.error?.message || 'Failed to add to cart'
                );
              },
            });
        },
      });
      return;
    }

    this.loading = true;
    this._cartStateService
      .addToCartWithRecovery(payload, this.cart.uid)
      .subscribe({
        next: () => {
          this.loading = false;
          this._modalRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          const msg =
            err.error?.message || err.error?.[0] || 'Failed to add to cart';
          this._notification.error('Adding Failed', msg);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  public cancel(): void {
    this._modalRef.close();
  }

  public checkAddMoreContent() :void{
    const isHacApp = this._authStateService.getAppType() === 'hac';
    this._modal.confirm({
      nzTitle: 'Product added to cart',
      nzContent: 'Do you want to proceed to cart',
      nzOkText: 'Yes Proceed to cart',
      nzCancelText: 'No add more tickets',
      nzOnOk: () => this.proceedToCart(),
      nzOnCancel: () => {
        if (isHacApp) {
          // HAC enforces one ticket per event per member, so once a ticket
          // is added the user is sent back to browse other events.
          this._router.navigate(['/']).then(() => {
            setTimeout(() => {
              const eventsSection = document.getElementById('active-events');
              if (eventsSection) {
                eventsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          });
        }
        // Non-HAC: stay on the current event page (don't navigate away)
        // so the user can re-open the AddToCart modal and add another
        // ticket with fresh field values. The confirm dialog has already
        // been dismissed, nothing more to do.
      }
    });
  }

  public proceedToCart() :void {
    if (!this.cart) return;
    // Soft navigation keeps CartStateService cache alive across the route change.
    this._router.navigateByUrl('/cart/' + this.cart.uid);
  }
}
